import type { Map } from 'mapbox-gl';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  MAPBOX_BASEMAP_MASK_LAYER_ID,
  MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID,
  MAPBOX_BLUEPRINT_LAYER_ID,
} from '../map.constants';
import { MapLayerIds, MAP_THREEJS_LAYER_IDS } from './map-config.constants';
import { moveLayerBeforeIfNeeded } from './map-layer-order.helpers';

const BASEMAP_DIM_START_PITCH = 75;
const BASEMAP_DIM_END_PITCH = 85;
const BASEMAP_DIM_END_MASK_OPACITY = 0.9;

/** White/gray cover opacity that leaves the basemap at 100% → 10% over 75°–85°. */
export const getBasemapDimMaskOpacity = (pitch: number): number => {
  if (pitch <= BASEMAP_DIM_START_PITCH) return 0;
  if (pitch >= BASEMAP_DIM_END_PITCH) {
    return BASEMAP_DIM_END_MASK_OPACITY;
  }
  return (
    ((pitch - BASEMAP_DIM_START_PITCH) /
      (BASEMAP_DIM_END_PITCH - BASEMAP_DIM_START_PITCH)) *
    BASEMAP_DIM_END_MASK_OPACITY
  );
};

/** First overlay that should sit above the gray plate (bottom → top preference). */
const BASEMAP_COVER_BEFORE_LAYER_IDS = [
  MAPBOX_BLUEPRINT_LAYER_ID,
  MAP_THREEJS_LAYER_IDS.facadeSky,
  MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID,
  MAP_THREEJS_LAYER_IDS.extrusion,
  MapLayerIds.OtherBuildingsFill,
  MapLayerIds.BuildingFill2d,
  MapLayerIds.Points,
] as const;

type MapWithSlot = {
  getLayer: (id: string) => unknown;
  getSlot?: (layerId: string) => string | null | undefined;
  setSlot?: (layerId: string, slot?: string | null) => unknown;
};

/**
 * Layer the gray plate should sit immediately below, so PDF and buildings
 * stay visible while the plate covers the Mapbox Standard import.
 */
export const resolveBasemapCoverBeforeId = (
  map: Pick<MapWithSlot, 'getLayer'>,
): string | undefined =>
  BASEMAP_COVER_BEFORE_LAYER_IDS.find((layerId) => map.getLayer(layerId));

/**
 * Lift the plate out of slot `top` so Standard layers cannot paint above it
 * when the import re-merges on zoom.
 */
export const placeBasemapCoverAboveImports = (map: Map): void => {
  if (!map.getLayer(MAPBOX_BASEMAP_MASK_LAYER_ID)) return;
  clearBasemapCoverSlot(map);
  const beforeId = resolveBasemapCoverBeforeId(map);
  if (!beforeId) return;
  moveLayerBeforeIfNeeded(map, MAPBOX_BASEMAP_MASK_LAYER_ID, beforeId);
};

/**
 * Drop a Standard slot on the gray plate without {@link Map.moveLayer}.
 * Zoom can re-slot the fill into `top`; clearing is enough to keep streets under the plate.
 */
export const clearBasemapCoverSlot = (map: MapWithSlot): void => {
  if (!guards.is.function(map.getSlot) || !guards.is.function(map.setSlot)) {
    return;
  }
  const currentSlot = map.getSlot(MAPBOX_BASEMAP_MASK_LAYER_ID);
  if (currentSlot == null) return;
  map.setSlot(MAPBOX_BASEMAP_MASK_LAYER_ID, null);
};
